import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useApi } from './useApi';
import { useScreenModes } from '@/providers/ScreenModesProvider';
import { useCompany } from '@/contexts/CompanyContext';
import type { UserContextApi } from '@/types/api/user-context';

const mockUserContext: UserContextApi = {
  evoOrganisationId: 'org-001',
  userId: 'user-001',
  email: 'sarah.johnson@company.com',
  firstName: 'Sarah',
  lastName: 'Johnson',
  evoSubscriptionId: 'sub-001',
  companyId: null,
  organisationId: null,
  internalUserId: null,
  companies: [
    { id: 1, name: 'Acme Corporation', currencyCode: 'GBP', isMultiCompanyGroup: true, linkedDatabaseName: null, linkedProductName: 'Access Financials' },
    { id: 2, name: 'TechStart Inc.', currencyCode: 'USD', isMultiCompanyGroup: false, linkedDatabaseName: null, linkedProductName: 'Access Financials' },
    { id: 3, name: 'Global Solutions Ltd.', currencyCode: 'EUR', isMultiCompanyGroup: true, linkedDatabaseName: null, linkedProductName: 'Access Dimensions' },
    { id: 4, name: 'Innovation Partners', currencyCode: 'GBP', isMultiCompanyGroup: false, linkedDatabaseName: null, linkedProductName: null },
    { id: 5, name: 'Enterprise Systems', currencyCode: 'GBP', isMultiCompanyGroup: false, linkedDatabaseName: null, linkedProductName: null },
  ],
  createdAt: '2023-01-10T08:00:00Z',
  role: 'Administrator',
  evoOrganisationName: 'Demo Organisation',
};

export function useUserContext() {
  const { apiGet } = useApi();
  const { getScreenMode } = useScreenModes();
  const { selectedCompany } = useCompany();
  const isDemo = getScreenMode('Settings_Users') === 'demo';

  return useQuery<UserContextApi, Error>({
    queryKey: ['usercontext', isDemo ? 'demo' : 'live', selectedCompany?.id ?? 'no-company'],
    queryFn: async ({ signal }) => {
      if (isDemo) {
        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(resolve, 300);
          if (signal) {
            signal.addEventListener('abort', () => {
              clearTimeout(timeout);
              reject(new DOMException('Aborted', 'AbortError'));
            });
          }
        });
        return mockUserContext;
      }
      return apiGet<UserContextApi>('/api/v1/usercontext', undefined, signal);
    },
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}
